import gqlClient from "./api/graphql.client";
import {
  DELETE_MANY_GROUP_BY_CREATOR,
  DELETE_MANY_GROUP_MEMBERSHIP_BY_USER,
} from "./api/groups/groups.query";
import { DELETE_MANY_NEWS_BY_CREATOR } from "./api/news/news.query";
import {
  DELETE_MANY_EVENT_BY_CREATOR,
  DELETE_MANY_EVENT_GALLERY_BY_CREATOR,
  DELETE_MANY_RSVP_BY_CREATOR,
} from "./api/events/events.query";
import {
  DELETE_MANY_COMMENT_BY_AUTHOR,
  DELETE_MANY_POST_BY_CREATOR,
  DELETE_MANY_REACTION_BY_USER,
} from "./api/posts/posts.query";
import { DELETE_MANY_USER_PROFILE_BY_CREATOR } from "./profile/profile.query";

export type PurgeStepResult = {
  entity: string;
  impacted: number;
  ok: boolean;
  error?: unknown;
};


export const purgeUserData = async (
  userId: string,
): Promise<PurgeStepResult[]> => {
  const client = await gqlClient();
  const results: PurgeStepResult[] = [];

  const steps: { entity: string; run: () => Promise<unknown> }[] = [
    {
      entity: "reactions",
      run: () =>
        client.request(DELETE_MANY_REACTION_BY_USER, { userId }),
    },
    {
      entity: "comments",
      run: () =>
        client.request(DELETE_MANY_COMMENT_BY_AUTHOR, { authorId: userId }),
    },
    {
      entity: "rsvps",
      run: () =>
        client.request(DELETE_MANY_RSVP_BY_CREATOR, { createdBy: userId }),
    },
    {
      entity: "eventGalleries",
      run: () =>
        client.request(DELETE_MANY_EVENT_GALLERY_BY_CREATOR, {
          createdBy: userId,
        }),
    },
    {
      entity: "groupMemberships",
      run: () =>
        client.request(DELETE_MANY_GROUP_MEMBERSHIP_BY_USER, { userId }),
    },

    {
      entity: "posts",
      run: () =>
        client.request(DELETE_MANY_POST_BY_CREATOR, { createdBy: userId }),
    },
    {
      entity: "news",
      run: () =>
        client.request(DELETE_MANY_NEWS_BY_CREATOR, { createdBy: userId }),
    },
    {
      entity: "events",
      run: () =>
        client.request(DELETE_MANY_EVENT_BY_CREATOR, { createdBy: userId }),
    },
    {
      entity: "groups",
      run: () =>
        client.request(DELETE_MANY_GROUP_BY_CREATOR, { createdBy: userId }),
    },
    // No leaderboard step: that collection is server-owned and the Function only
    // deletes the caller's own rows, so self-deletion calls it before deactivating
    // (settings/account). A user deleted by an admin drops off the board once the
    // reactions and comments above are gone.
    {
      entity: "userProfile",
      run: () =>
        client.request(DELETE_MANY_USER_PROFILE_BY_CREATOR, {
          createdBy: userId,
        }),
    },
  ];

  for (const step of steps) {
    try {
      const res = (await step.run()) as Record<string, any> | undefined;
      const payload = res ? Object.values(res)[0] : undefined;
      const impacted = (payload as any)?.totalImpactedData ?? 0;
      results.push({ entity: step.entity, impacted, ok: true });
    } catch (error) {
      console.warn(`purgeUserData: failed to delete ${step.entity}`, error);
      results.push({ entity: step.entity, impacted: 0, ok: false, error });
    }
  }

  return results;
};
